import { jsx, jsxs } from 'react/jsx-runtime';
import { useState } from 'react';
import { twMerge } from '@belong/common';
import { IconLeftCaret, IconRightCaret, IconPlay, IconDownload, IconCaretUp, IconCaretDown } from '@belong/icons';
import useEmblaCarousel from 'embla-carousel-react';
import { ButtonBase } from '../ButtonBase/ButtonBase.js';
import { Modal } from '../Modal/Modal.js';
import { Text } from '../Text/Text.js';
import { VideoPlayer } from '../VideoPlayer/VideoPlayer.js';
import styles from './MediaGallery.module.css.js';

const MAX_COLLAPSED_ITEMS = 4;
const MediaGalleryModal = ({
  media,
  isOpen,
  onClose,
  selectedIndex
}) => {
  const [carouselRef, carouselApi] = useEmblaCarousel({
    loop: true,
    startIndex: selectedIndex
  });
  return jsx(Modal, {
    name: "media-gallery-modal",
    isOpen: isOpen,
    onDismiss: onClose,
    size: "fullScreen",
    closeButton: "content",
    children: jsxs("div", {
      className: "align-center flex",
      children: [media.length > 1 && jsx(ButtonBase, {
        className: "hidden bg-white p-2sm md:block",
        onClick: () => carouselApi?.scrollPrev(),
        children: jsx(IconLeftCaret, {
          className: "text-navy",
          height: 20
        })
      }), jsx("div", {
        ref: carouselRef,
        className: "flex-1 overflow-hidden",
        children: jsx("div", {
          className: "flex",
          children: media.map(mediaItem => jsxs("div", {
            className: "flex h-screen w-screen shrink-0 grow-0 basis-full flex-row items-center justify-center",
            children: [mediaItem.mediaType === 'Image' && jsx("img", {
              src: mediaItem.url,
              alt: "preview",
              className: "h-auto max-h-full w-auto max-w-full object-contain"
            }), mediaItem.mediaType === 'Video' && jsx(VideoPlayer, {
              playsInline: true,
              videoSrc: mediaItem.url,
              posterSrc: "",
              autoplay: false
            })]
          }, mediaItem.uniqueId))
        })
      }), media.length > 1 && jsx(ButtonBase, {
        className: "hidden bg-white p-2sm md:block",
        onClick: () => carouselApi?.scrollNext(),
        children: jsx(IconRightCaret, {
          className: "text-navy",
          height: 20
        })
      })]
    })
  });
};
const MediaGallery = ({
  media
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCarouselVisible, setIsCarouselVisible] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const carouselMedia = media.filter(
  // @ts-ignore
  item => !!item.url && (item.mediaType === 'Video' || item.mediaType === 'Image'));
  const documentMedia = media.filter(
  // @ts-ignore
  item => !!item.url && item.mediaType === 'Pdf');
  const supportedMedia = carouselMedia.concat(documentMedia);
  const onCloseClick = () => setIsCarouselVisible(false);
  const handlePreviewClick = (mediaItem, index) => {
    if (mediaItem.mediaType === 'Pdf') {
      window.open(mediaItem.url, '_blank');
      return;
    }
    setIsCarouselVisible(true);
    setSelectedImageIndex(index);
  };
  return jsxs("div", {
    children: [jsx("div", {
      className: "grid  grid-cols-2 gap-2sm sm:grid-cols-4",
      children: supportedMedia.slice(0, isExpanded ? supportedMedia.length : MAX_COLLAPSED_ITEMS).map((mediaItem, index) => jsx("div", {
        className: twMerge('w-full h-full rounded flex items-center justify-center overflow-hidden bg-dark-navy', mediaItem.mediaType === 'Pdf' && 'border border-solid border-gray', styles.previewItem),
        children: jsxs(ButtonBase, {
          className: "h-full w-full hover:opacity-70",
          onClick: () => handlePreviewClick(mediaItem, index),
          children: [mediaItem.mediaType === 'Image' && jsx("img", {
            src: mediaItem.url,
            alt: "preview",
            className: "h-full w-full"
          }), mediaItem.mediaType === 'Video' && jsx("div", {
            className: "flex h-full w-full items-center justify-center",
            children: jsx(IconPlay, {
              className: "text-white",
              height: 40
            })
          }), mediaItem.mediaType === 'Pdf' && jsx("div", {
            className: "flex h-full w-full items-center justify-center",
            children: jsx(IconDownload, {
              className: "text-white",
              height: 40
            })
          })]
        })
      }, mediaItem.uniqueId))
    }), supportedMedia.length > MAX_COLLAPSED_ITEMS && jsx("div", {
      className: "mt-sm flex w-full flex-col items-end",
      children: jsxs(ButtonBase, {
        onClick: () => setIsExpanded(!isExpanded),
        className: "flex items-center",
        children: [jsx(Text, {
          fontWeight: "semibold",
          children: isExpanded ? 'SHOW LESS' : 'SHOW MORE'
        }), isExpanded ? jsx(IconCaretUp, {
          className: "ml-xs text-navy",
          height: 8
        }) : jsx(IconCaretDown, {
          className: "ml-xs text-navy",
          height: 8
        })]
      })
    }), jsx(MediaGalleryModal, {
      isOpen: isCarouselVisible,
      media: carouselMedia,
      onClose: onCloseClick,
      selectedIndex: selectedImageIndex
    })]
  });
};

export { MediaGallery, MediaGalleryModal };
